import { Link } from "react-router-dom";
import axios from "axios";
import { PlayCircle, ArrowRight, Star, MapPin } from "lucide-react";
import { API } from "@/portal/AuthContext";

// „So sieht's bei deinen Kunden aus“: zeigt die Demo-Mini-Website + Demo-Website als Mockups.
// Nur eigene/KI-Bilder (Robbies Kaffeeküche, fiktiv), klar als Beispiel gekennzeichnet.
// Klicks auf „Demo-Bereich anschauen“ werden anonym je Quell-Seite gezählt.
const track = (quelle) => { try { axios.post(`${API}/stats/demo-cta`, { quelle }, { headers: { "X-Requested-With": "XMLHttpRequest" } }); } catch { /* egal */ } };

const DemoBadge = ({ className = "" }) => (
  <span className={`inline-flex items-center gap-1 rounded-full bg-[#2a1a0e]/85 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#FBF4E8] backdrop-blur ${className}`}>Beispiel · Demo</span>
);

const Foto = ({ src, alt, w, h, className }) => (
  <img src={src} alt={alt} loading="lazy" decoding="async" width={w} height={h} className={className} />
);

const PhoneMock = () => (
  <div className="relative w-[172px] shrink-0 sm:w-[190px]" data-testid="demo-mock-phone">
    <div className="overflow-hidden rounded-[2rem] border-[7px] border-[#17120d] bg-[#17120d] shadow-[0_18px_40px_-12px_rgba(42,26,14,0.55)]">
      <div className="relative aspect-[9/18] overflow-hidden rounded-[1.4rem] bg-[#FBF4E8]">
        <Foto src="/demo/cafe_interior.webp" alt="Beispiel-Mini-Website von Robbies Kaffeeküche" w={190} h={200} className="h-[46%] w-full object-cover" />
        <DemoBadge className="absolute left-2 top-2" />
        <div className="relative -mt-7 flex flex-col items-center px-3 text-center">
          <Foto src="/demo/cafe_logo.webp" alt="" w={52} h={52} className="h-[52px] w-[52px] rounded-full border-[3px] border-[#FBF4E8] object-cover shadow-md" />
          <p className="mt-1.5 font-display text-[13px] font-extrabold leading-none text-[#2a1a0e]">Robbies Kaffeeküche</p>
          <p className="mt-1 text-[8px] text-[#7a6552]">Kaffee · Kuchen · gute Laune · Herford</p>
          <div className="mt-2.5 grid w-full grid-cols-3 gap-1">
            <Foto src="/demo/latte_art.webp" alt="" w={56} h={56} className="aspect-square w-full rounded-md object-cover" />
            <Foto src="/demo/cheesecake.webp" alt="" w={56} h={56} className="aspect-square w-full rounded-md object-cover" />
            <Foto src="/demo/cold_brew.webp" alt="" w={56} h={56} className="aspect-square w-full rounded-md object-cover" />
          </div>
          <span className="mt-2.5 inline-block rounded-full bg-[#B88E3E] px-3 py-1 text-[9px] font-bold text-white">Speisekarte ansehen</span>
        </div>
      </div>
    </div>
  </div>
);

const DesktopMock = () => (
  <div className="relative hidden w-full max-w-[420px] sm:block" data-testid="demo-mock-desktop">
    <div className="overflow-hidden rounded-xl border border-[#E4D8C4] bg-white shadow-[0_18px_44px_-14px_rgba(42,26,14,0.45)]">
      <div className="flex items-center gap-1.5 border-b border-[#EFE6D6] bg-[#F7F0E4] px-3 py-2">
        <span className="h-2.5 w-2.5 rounded-full bg-[#E4B4B4]" /><span className="h-2.5 w-2.5 rounded-full bg-[#EBD79A]" /><span className="h-2.5 w-2.5 rounded-full bg-[#B8D8B0]" />
        <span className="ml-2 truncate rounded-md bg-white px-2 py-0.5 text-[10px] text-[#8c7a63]">robbies-kaffeekueche.demo.dimedial.studio</span>
      </div>
      <div className="relative">
        <Foto src="/demo/cafe_interior.webp" alt="Beispiel-Website von Robbies Kaffeeküche" w={420} h={200} className="h-[150px] w-full object-cover" />
        <DemoBadge className="absolute right-2 top-2" />
        <div className="absolute inset-0 flex flex-col items-start justify-end bg-gradient-to-t from-[#2a1a0e]/70 to-transparent p-3">
          <Foto src="/demo/cafe_logo.webp" alt="" w={34} h={34} className="h-[34px] w-[34px] rounded-full border-2 border-white object-cover" />
          <p className="mt-1 font-display text-lg font-extrabold leading-none text-white">Robbies Kaffeeküche</p>
          <p className="text-[10px] text-white/85">Dein Lieblingsplatz für Kaffee & Kuchen in Herford</p>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 p-3">
        {[["/demo/latte_art.webp", "Flat White", "3,80 €"], ["/demo/cold_brew.webp", "Zimt-Cold-Brew", "3,50 €"], ["/demo/cheesecake.webp", "Kürbis-Cheesecake", "4,50 €"]].map(([s, t, p]) => (
          <div key={t} className="overflow-hidden rounded-lg border border-[#EFE6D6]">
            <Foto src={s} alt="" w={120} h={70} className="h-[64px] w-full object-cover" />
            <div className="px-1.5 py-1"><p className="truncate text-[9px] font-bold text-[#2a1a0e]">{t}</p><p className="text-[9px] text-[#B88E3E]">{p}</p></div>
          </div>
        ))}
      </div>
    </div>
    <div className="absolute -bottom-7 -left-6 hidden w-[188px] rounded-xl border border-[#E4D8C4] bg-white p-2.5 shadow-[0_14px_30px_-10px_rgba(42,26,14,0.4)] md:block" data-testid="demo-mock-google">
      <div className="flex items-center gap-2">
        <span className="grid h-7 w-7 place-items-center rounded-full bg-[#F2F5FB] text-[11px] font-black text-[#4285F4]">G</span>
        <div className="min-w-0"><p className="truncate text-[11px] font-bold text-[#2a1a0e]">Robbies Kaffeeküche</p><p className="text-[9px] text-[#6b5a48]">Café · Herford</p></div>
      </div>
      <div className="mt-1.5 flex items-center gap-1">
        <span className="text-[11px] font-bold text-[#2a1a0e]">4,9</span>
        {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={10} className="fill-[#FBBC04] text-[#FBBC04]" />)}
        <span className="text-[9px] text-[#6b5a48]">(128)</span>
      </div>
      <div className="mt-1.5 grid grid-cols-3 gap-1">
        <Foto src="/demo/barista_pour.webp" alt="" w={56} h={40} className="h-10 w-full rounded object-cover" />
        <Foto src="/demo/latte_art.webp" alt="" w={56} h={40} className="h-10 w-full rounded object-cover" />
        <Foto src="/demo/cheesecake.webp" alt="" w={56} h={40} className="h-10 w-full rounded object-cover" />
      </div>
      <p className="mt-1.5 flex items-center gap-1 text-[9px] font-semibold text-[#1a7f37]"><MapPin size={9} /> Als Geschäft verifiziert</p>
    </div>
  </div>
);

const CtaButtons = ({ source, center }) => (
  <div className={`flex flex-wrap gap-3 ${center ? "justify-center" : ""}`} data-testid="demo-showcase-cta">
    <Link to="/live-demo" onClick={() => track(source)} data-testid={`demo-anschauen-${source}`}
      className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#111] bg-[#FDD105] px-6 py-3.5 text-[15px] font-bold text-[#111] shadow-[0_6px_0_0_#111] transition-transform hover:-translate-y-0.5 active:translate-y-0 active:shadow-[0_3px_0_0_#111]">
      <PlayCircle size={18} strokeWidth={2.5} /> Demo-Bereich anschauen
    </Link>
    <Link to="/kunden/registrieren" data-testid={`demo-testen-${source}`}
      className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#111] bg-white px-6 py-3.5 text-[15px] font-bold text-[#111] transition-transform hover:-translate-y-0.5 active:translate-y-0">
      30 Tage kostenlos testen <ArrowRight size={18} strokeWidth={2.5} />
    </Link>
  </div>
);

/** variant "full" (Startseite) oder "compact" (Landingpages). source = Quell-Seite für den Klick-Zähler. */
export const DemoShowcase = ({ variant = "full", source = "unbekannt", highlightGastro = false }) => {
  const compact = variant === "compact";
  return (
    <section data-testid={`demo-showcase-${source}`} className="relative overflow-hidden rounded-3xl border border-[#EADFCB] bg-gradient-to-br from-[#FBF4E8] via-[#F6ECDA] to-[#F0E3CC] px-5 py-8 sm:px-8 sm:py-10">
      <div className={`mx-auto grid max-w-5xl items-center gap-8 ${compact ? "md:grid-cols-[1fr_auto]" : "md:grid-cols-2"}`}>
        <div className={compact ? "" : "order-2 md:order-1"}>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#B88E3E]">Echte Demo zum Anfassen</p>
          <h2 className="mt-2 font-display text-2xl font-extrabold leading-tight text-[#2a1a0e] sm:text-3xl" data-testid="demo-showcase-title">So sieht's bei deinen Kunden aus</h2>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-[#5c4a38] sm:text-base">
            {highlightGastro
              ? "Schau dir unser Café-Beispiel „Robbies Kaffeeküche“ an: Mini-Website, Google-Profil, Beiträge, Bildschirme & Ticketing – alles in warmen Farben, in wenigen Minuten eingerichtet."
              : "Klick dich durch den kompletten Kundenbereich am Beispiel „Robbies Kaffeeküche“ – Mini-Website, Website, Beiträge, Google & mehr. Ganz ohne Anmeldung."}
          </p>
          <p className="mt-2 text-xs text-[#8c7a63]">Beispiel/Demo – fiktiver Betrieb, nur zur Ansicht. Eigene, KI-erstellte Bilder.</p>
          <div className="mt-5"><CtaButtons source={source} /></div>
        </div>
        <div className={`relative flex items-center justify-center gap-0 ${compact ? "" : "order-1 md:order-2"}`}>
          {!compact && <div className="pointer-events-none mr-[-2.75rem] hidden sm:block"><DesktopMock /></div>}
          <PhoneMock />
        </div>
      </div>
    </section>
  );
};
